"use client";

import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { useOAuthPending } from "@/components/ui/oauthProviderButtons/useOAuthPending";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UsersUserIdWelcomeActionsWelcomeActionIdDeleteRequest } from "@/generated/email_parser.openapi";
import {
  azureSignInReSync,
  googleSignInReSync,
  yahooSignInReSync,
} from "@/lib/signInUtils";
import type { DialogProps } from "@/components/ui/dialog";
import { useSession } from "next-auth/react";
import React from "react";

// Each scoped-auth provider gets its own name, logo, and re-sync flow so the
// "Keep your points up to date" prompt matches the inbox that actually expired
// (a user can have several synced, e.g. Gmail + Outlook).
type ProviderConfig = {
  label: string;
  logo: string;
  reSync: (welcomeActionId: string, userId: string) => Promise<unknown>;
};

const PROVIDER_CONFIG: Record<string, ProviderConfig> = {
  "google-scoped-auth": {
    label: "Gmail",
    logo: "/assets/logos/google.svg",
    reSync: googleSignInReSync,
  },
  "azure-ad-scoped-auth": {
    label: "Outlook",
    logo: "/assets/logos/microsoft.svg",
    reSync: azureSignInReSync,
  },
  "yahoo-scoped-auth": {
    label: "Yahoo",
    logo: "/assets/logos/yahoo.svg",
    reSync: yahooSignInReSync,
  },
};

const DEFAULT_PROVIDER_CONFIG = PROVIDER_CONFIG["google-scoped-auth"];

const GmailReSyncModal: React.FC<DialogProps> = (props) => {
  const { pendingId, error, handleSignIn } = useOAuthPending();
  const {
    welcomeActions: { data: welcomeActionData },
  } = useGlobalStore();
  const { data: session } = useSession();
  const welcomeActionId =
    welcomeActionData?.emailSyncTokenExpiredWelcomeAction?.id !== undefined
      ? welcomeActionData?.emailSyncTokenExpiredWelcomeAction?.id
      : null;

  const provider =
    welcomeActionData?.emailSyncTokenExpiredWelcomeAction?.provider;
  const providerConfig =
    (provider && PROVIDER_CONFIG[provider]) || DEFAULT_PROVIDER_CONFIG;

  const onDismiss = () => {
    deleteWelcomeAction();
    props.onOpenChange?.(false);
  };

  const deleteWelcomeAction = async () => {
    if (welcomeActionData?.emailSyncTokenExpiredWelcomeAction?.id) {
      const request: UsersUserIdWelcomeActionsWelcomeActionIdDeleteRequest = {
        userId: session?.user.id as string,
        welcomeActionId:
          welcomeActionData?.emailSyncTokenExpiredWelcomeAction.id,
      };
      await fetch("/api/user/welcome_actions/delete", {
        method: "POST",
        body: JSON.stringify(request),
      });
    }
  };

  return (
    <Dialog {...props} onOpenChange={onDismiss}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Keep your points up to date</DialogTitle>
          <DialogDescription>
            To maintain your data privacy and security, every six months we will
            ask you to update your {providerConfig.label} connection to continue
            automatically tracking your points balance.{" "}
            <a href="/sync" className="text-primary underline">
              You can read more here
            </a>
            .
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            className="whitespace-nowrap"
            loading={pendingId !== null}
            disabled={pendingId !== null}
            onClick={() =>
              handleSignIn({
                id: "re-sync",
                signIn: () =>
                  providerConfig.reSync(
                    `${welcomeActionId}`,
                    session?.user.id as string
                  ),
              })
            }
          >
            <img
              src={providerConfig.logo}
              alt={providerConfig.label}
              className="h-5 w-5"
            />
            Update {providerConfig.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GmailReSyncModal;
